import React, { useState, useEffect } from 'react';
import { useSaaSContext } from '../../store/SaaSContext';
import { 
  Download, 
  AlertCircle, 
  Loader2, 
  CheckCircle2, 
  Info, 
  Printer, 
  Layers, 
  FileText,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Zap,
  Check,
  Eye,
  FileCheck2,
  SlidersHorizontal
} from 'lucide-react';
import { ProductionRoiVisualizer } from './ProductionRoiVisualizer';
import { useRealtimeCurrency } from '../../hooks/useRealtimeCurrency';
import { findSubstratePreset } from '../../utils/substratePresets';
import { SubstrateCustomizerModal } from './SubstrateCustomizerModal';
import { JobTicketModal } from './JobTicketModal';
import { getClientAuthToken } from '../../utils/authUtils';
import { CurrencySyncSelector } from './CurrencySyncSelector';
import { useToast } from '../../store/ToastContext';
import { VdpLayout, VdpTemplate } from '../../types';

interface ExportHardwareProfile {
  id: string;
  name: string;
  tagline: string;
  badge: string;
  apply: (currentLayout: VdpLayout, template: VdpTemplate) => VdpLayout;
}

const EXPORT_HARDWARE_PROFILES: ExportHardwareProfile[] = [
  {
    id: 'commercial-gang',
    name: 'HP Indigo / Heidelberg Commercial Press',
    tagline: 'Multi-up A3/A4 gang imposition with CMYK color bars, crop marks, and slug',
    badge: 'Commercial Press',
    apply: (l) => ({
      ...l,
      pageSize: 'A3',
      pageOrientation: 'portrait',
      rows: 6,
      cols: 3,
      gapX: 8,
      gapY: 8,
      marginX: 20,
      marginY: 20,
      bleed: 3,
      showTrimMarks: true,
      trimMarkType: 'all_marks',
      useCmyk: true,
      showColorBars: true,
      showRegistrationMarks: true,
      showSlug: true,
      exportMode: 'multi_up'
    })
  },
  {
    id: 'plotter-cutter',
    name: 'Roland / Graphtec Vinyl Contour Cutter',
    tagline: 'Sheet layout with 2mm bleed allowance and perimeter registration crop marks',
    badge: 'Plotter & Cutter',
    apply: (l) => ({
      ...l,
      pageSize: 'A4',
      pageOrientation: 'portrait',
      rows: 4,
      cols: 2,
      gapX: 10,
      gapY: 10,
      marginX: 18,
      marginY: 18,
      bleed: 2,
      showTrimMarks: true,
      trimMarkType: 'outer_grid',
      exportMode: 'multi_up'
    })
  },
  {
    id: 'zebra-thermal',
    name: 'Zebra / TSC Direct Thermal Roll Label',
    tagline: '1-up continuous roll feed format with zero margin waste for thermal printers',
    badge: 'Thermal Roll',
    apply: (l, t) => ({
      ...l,
      isRollMode: true,
      pageSize: 'custom',
      pageWidth: Math.round(Number(t.width) || 250) + 10,
      pageHeight: Math.round(Number(t.height) || 150) + 10,
      rows: 1,
      cols: 1,
      gapX: 0,
      gapY: 0,
      marginX: 5,
      marginY: 5,
      bleed: 0,
      showTrimMarks: false,
      exportMode: 'single_pdf'
    })
  },
  {
    id: 'desktop-proofing',
    name: 'Standard Office / Inkjet Proofing',
    tagline: 'Clean A4 multi-up layout with standard gutters for fast desktop sample prints',
    badge: 'Desktop Proof',
    apply: (l) => ({
      ...l,
      isRollMode: false,
      pageSize: 'A4',
      pageOrientation: 'portrait',
      rows: 3,
      cols: 2,
      gapX: 12,
      gapY: 12,
      marginX: 20,
      marginY: 20,
      bleed: 1,
      showTrimMarks: false,
      exportMode: 'multi_up'
    })
  },
  {
    id: 'mimaki-contour',
    name: 'Mimaki FineCut Contour System',
    tagline: 'Sheet layout with Inverted-T corner marks and 2mm bleed for Mimaki CG cutters',
    badge: 'Mimaki System',
    apply: (l) => ({
      ...l,
      isRollMode: false,
      pageSize: 'A4',
      pageOrientation: 'portrait',
      rows: 4,
      cols: 2,
      gapX: 8,
      gapY: 8,
      marginX: 16,
      marginY: 16,
      bleed: 2,
      plotterMarks: true,
      plotterMarkType: 'mimaki',
      plotterMarkOffset: 14,
      plotterMarkSize: 12,
      showTrimMarks: false,
      exportMode: 'multi_up'
    })
  },
  {
    id: 'mutoh-valuecut',
    name: 'Mutoh ValueCut Plotter System',
    tagline: 'Multi-up imposition with dual-axis quiet-ring marks for ValueCut optical sensors',
    badge: 'Mutoh ValueCut',
    apply: (l) => ({
      ...l,
      isRollMode: false,
      pageSize: 'A4',
      pageOrientation: 'portrait',
      rows: 4,
      cols: 2,
      gapX: 10,
      gapY: 10,
      marginX: 18,
      marginY: 18,
      bleed: 2,
      plotterMarks: true,
      plotterMarkType: 'mutoh',
      plotterMarkOffset: 15,
      plotterMarkSize: 14,
      showTrimMarks: false,
      exportMode: 'multi_up'
    })
  }
];

export const ProductionStep = () => {
  const { csvData, template, layout, setLayout } = useSaaSContext();
  const { toast } = useToast();
  const [selectedProfileId, setSelectedProfileId] = useState<string>('commercial-gang');

  const handleApplyProfile = (profileId: string) => {
    const profile = EXPORT_HARDWARE_PROFILES.find(p => p.id === profileId);
    if (!profile) return;
    setSelectedProfileId(profileId);
    const updated = profile.apply(layout, template);
    setLayout(updated);
    toast.success('Hardware Profile Loaded', `Switched layout parameters to ${profile.name}`);
  };

  const { 
    activeSymbol, 
    format, 
    refreshRates,
    isRefreshing
  } = useRealtimeCurrency();

  const [isExporting, setIsExporting] = useState(false);
  const [isExportingProof, setIsExportingProof] = useState(false);
  const [exportProgressStage, setExportProgressStage] = useState<string>('');
  const [exportProgressPercent, setExportProgressPercent] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  // Modals
  const [showSubstrateModal, setShowSubstrateModal] = useState(false);
  const [showJobTicketModal, setShowJobTicketModal] = useState(false);
  const [showPreflightDetails, setShowPreflightDetails] = useState(false);

  // Job Docket Metadata
  const [workOrderNumber, setWorkOrderNumber] = useState<string>(() => `WO-${Math.floor(100000 + Math.random() * 900000)}`);
  const [clientName, setClientName] = useState<string>('');
  const [operatorNotes, setOperatorNotes] = useState<string>('');

  const totalRecords = csvData?.rows.length || 0;
  
  const [exportStart, setExportStart] = useState<number>(1);
  const [exportEnd, setExportEnd] = useState<number>(totalRecords || 1);

  // Sync exportEnd when totalRecords changes
  useEffect(() => {
    if (totalRecords > 0) {
      setExportEnd(totalRecords);
    }
  }, [totalRecords]);

  const rows = Number(layout.rows) || 1;
  const cols = Number(layout.cols) || 1;
  const disabledCount = Array.isArray(layout.disabledSlots) ? layout.disabledSlots.length : 0;
  const effectiveSlotsPerPage = layout.exportMode === 'single_pdf' || layout.exportMode === 'separate_pdfs' 
    ? 1 
    : Math.max(1, (rows * cols) - disabledCount);
  const stickersPerPage = layout.exportMode === 'single_pdf' || layout.exportMode === 'separate_pdfs' ? 1 : (rows * cols);
  
  const selectedRecords = Math.max(0, exportEnd - exportStart + 1);
  const totalPages = Math.ceil(selectedRecords / effectiveSlotsPerPage);

  // Substrate & Material Economics
  const substrate = findSubstratePreset(layout.substrateType || 'standard_vinyl');
  const substrateName = layout.substrateName || substrate.name;
  const substrateGsm = layout.substrateGsm !== undefined ? layout.substrateGsm : substrate.weightGsm;
  const unitCost = layout.costPerSheet !== undefined ? layout.costPerSheet : substrate.defaultCost;
  const inkCost = layout.inkCostPerSheet !== undefined ? layout.inkCostPerSheet : 0.8;
  const laborRate = layout.laborRatePerHour !== undefined ? layout.laborRatePerHour : 300;
  const pressPpm = layout.pressSpeedPpm !== undefined ? layout.pressSpeedPpm : 45;

  // Imposition Savings
  const singleSheets = selectedRecords;
  const multiSheets = totalPages;
  const savedSheets = Math.max(0, singleSheets - multiSheets);
  const totalMaterialCost = totalPages * unitCost;
  const totalInkCost = totalPages * inkCost;
  const pressRunMinutes = pressPpm > 0 ? (totalPages / pressPpm) : 0;
  const pressLaborCost = (pressRunMinutes / 60) * laborRate;
  const totalJobCost = totalMaterialCost + totalInkCost + pressLaborCost;
  const savedMoney = savedSheets * unitCost;
  const wasteReductionPct = singleSheets > 0 ? ((savedSheets / singleSheets) * 100).toFixed(0) : '0';

  // Paper Weight in Kg (A4 ~ 0.0624 m2, A3 ~ 0.125 m2)
  const isA3 = (layout.pageSize || '').toLowerCase().includes('a3') || (layout.pageSize || '').includes('12x18');
  const sheetAreaM2 = isA3 ? 0.125 : 0.0624;
  const totalPaperWeightKg = ((totalPages * sheetAreaM2 * substrateGsm) / 1000).toFixed(2);

  // Pre-Flight Diagnostic Checks
  const unmappedVariables = template.elements.filter(el => 
    el.type === 'text' && el.text?.includes('{') && csvData && !csvData.headers.some(h => el.text?.includes(`{${h}}`))
  );
  const hasUnmappedVariables = unmappedVariables.length > 0;
  
  const hasMarks = layout.printSlug || layout.colorBars || layout.registrationMarks || layout.showTrimMarks;
  const highVolumeWarning = totalRecords > 500;

  // Barcode & QR code count
  const barcodeCount = template.elements.filter(el => ['barcode', 'qr', 'datamatrix', 'ean13', 'upca'].includes(el.type)).length;

  const handleExport = async (isProof = false) => {
    if (isProof) {
      setIsExportingProof(true);
    } else {
      setIsExporting(true);
    }
    setError(null);
    setExportProgressPercent(5);
    setExportProgressStage('Validating dataset records and pre-press geometry...');

    let progressInterval: any = null;

    try {
      // Validate range
      const rowsToUse = csvData && Array.isArray(csvData.rows) && csvData.rows.length > 0 ? csvData.rows : [{}];
      const totalRecs = rowsToUse.length;
      const startIndex = Math.max(0, exportStart - 1);
      const endIndex = Math.min(totalRecs, exportEnd || totalRecs);
      
      // For proof, we take enough rows to fill one sheet from the selected range
      const selectedData = rowsToUse.slice(startIndex, endIndex);
      const dataToExport = isProof ? selectedData.slice(0, stickersPerPage) : selectedData;

      // Dynamic non-blocking progress ticker for real-time user feedback during large dataset compilation
      let currentProgress = 10;
      progressInterval = setInterval(() => {
        if (currentProgress < 90) {
          // Increment smaller as progress increases to prevent reaching 100% early
          const step = currentProgress < 40 ? 5 : currentProgress < 70 ? 3 : 1;
          currentProgress += step;
          setExportProgressPercent(currentProgress);

          if (currentProgress === 20) {
            setExportProgressStage('Generating dynamic vector barcodes & 2D QR matrices...');
          } else if (currentProgress === 45) {
            setExportProgressStage(`Imposing ${stickersPerPage}-up layout on ${layout.pageSize} master sheets...`);
          } else if (currentProgress === 75) {
            setExportProgressStage('Compiling high-resolution PDF/X-1a stream with registration marks...');
          } else if (currentProgress === 85) {
            setExportProgressStage('Compressing production stream & final graphics caching...');
          }
        }
      }, 350);

      const payload = {
        layout: {
          ...layout,
          workOrderNumber,
          clientName,
          operatorNotes
        },
        template,
        data: dataToExport
      };

      const appUrl = (import.meta as any).env?.VITE_APP_URL || '';
      
      const headers: Record<string, string> = { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getClientAuthToken()}`
      };
      
      const response = await fetch(appUrl ? `${appUrl}/api/generate` : '/api/generate', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      if (progressInterval) clearInterval(progressInterval);

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Server error: ${response.status}`);
      }

      setExportProgressPercent(100);
      setExportProgressStage('Preparing download package...');

      const blob = await response.blob();
      const isZip = blob.type === 'application/zip' || layout.exportMode === 'separate_pdfs' || layout.exportMode === 'separate_multi_up_pdfs';
      const extension = isZip ? 'zip' : 'pdf';
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      let downloadName = `${workOrderNumber || 'VDP'}_Production_${layout.pageSize}_${totalPages}Sheets`;
      if (!isProof && (layout.fileNameColumn || layout.fileNameSlug)) {
        let baseName = 'record_1';
        if (layout.fileNameColumn && dataToExport.length > 0) {
          const firstRecord = dataToExport[0];
          if (firstRecord[layout.fileNameColumn] !== undefined && firstRecord[layout.fileNameColumn] !== '') {
            baseName = String(firstRecord[layout.fileNameColumn]).replace(/[\\/:"*?<>|]/g, '-');
          }
        }
        let slug = layout.fileNameSlug ? String(layout.fileNameSlug).replace(/[\\/:"*?<>|]/g, '-') : '';
        downloadName = `${baseName}${slug}`;
      }

      a.download = isProof ? `${workOrderNumber || 'VDP'}_Digital_Proof_Sheet.${extension}` : `${downloadName}.${extension}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'An unexpected error occurred during export.');
    } finally {
      if (progressInterval) clearInterval(progressInterval);
      if (isProof) setIsExportingProof(false);
      else setIsExporting(false);
      setExportProgressPercent(0);
      setExportProgressStage('');
    }
  };

  return (
    <div className="max-w-5xl mx-auto flex flex-col gap-6">
      
      {/* Header & Live Currency Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-zinc-900 p-5 rounded-md border border-zinc-800 shadow-xs">
        <div>
          <div className="flex items-center gap-2.5 flex-wrap">
            <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight">
              Production Export &amp; Pre-Flight
            </h2>
            <span className="px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-extrabold flex items-center gap-1 border border-emerald-300">
              <ShieldCheck className="w-3.5 h-3.5" />
              100% Pre-Flight Verified
            </span>
          </div>
          <p className="text-xs sm:text-sm text-zinc-500 mt-0.5">
            Review your job summary and generate the final print-ready PDF file.
          </p>
        </div>

        {/* Real-time Currency Conversion Auto-Sync Widget */}
        <div className="flex items-center gap-2 self-start sm:self-auto bg-zinc-900 text-white px-3 py-2 rounded-md shadow-xs border border-zinc-800">
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider flex items-center gap-1">
              <Zap className="w-3 h-3 text-emerald-400 fill-emerald-400" />
              Realtime Currency
            </span>
            <span className="text-xs font-mono font-bold text-emerald-400">
              Auto-Synced
            </span>
          </div>

          <CurrencySyncSelector compact={true} showExchangeRate={false} />
          
          <button
            type="button"
            onClick={refreshRates}
            disabled={isRefreshing}
            className="p-1 text-zinc-400 hover:text-white rounded hover:bg-zinc-800 transition-colors cursor-pointer"
            title="Refresh Live Exchange Rates"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin text-emerald-400' : ''}`} />
          </button>
        </div>
      </div>

      {!csvData ? (
        <div className="flex items-center gap-3 p-5 rounded-md bg-amber-950/40 border border-amber-800/60 text-amber-200">
          <AlertCircle className="w-6 h-6 shrink-0 text-amber-500" />
          <div>
            <h4 className="font-bold text-sm text-amber-100">No Database Dataset Loaded</h4>
            <p className="text-xs text-amber-300/80 mt-0.5">
              Please go back to the Data Step and upload a CSV or spreadsheet to populate dynamic variable records.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          
          {/* Pre-Flight Inspection Status Strip */}
          <div className="bg-emerald-950/20 border border-emerald-500/30 rounded-xl p-4 sm:p-5 shadow-xs">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center border border-emerald-500/30 shrink-0">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-extrabold text-sm sm:text-base text-zinc-100 tracking-tight">
                      Commercial Press Pre-Flight Passed
                    </h3>
                    <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-[10px] font-mono font-bold rounded-full border border-blue-500/30">
                      PDF/X-1a Ready
                    </span>
                  </div>
                  <p className="text-xs text-zinc-400 mt-0.5">
                    {selectedRecords} Records • {stickersPerPage}-Up Grid • {totalPages} Master Sheets • Crisp Vector &amp; Barcode Engine
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => setShowPreflightDetails(!showPreflightDetails)}
                  className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-bold rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer border border-zinc-700"
                >
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                  <span>{showPreflightDetails ? 'Hide Pre-Flight Checks' : 'View 5 Pre-Flight Checks'}</span>
                  {showPreflightDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>

                <button
                  type="button"
                  onClick={() => setShowJobTicketModal(true)}
                  className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 transition-all shadow-sm cursor-pointer"
                >
                  <FileText className="w-3.5 h-3.5" />
                  <span>Work Order Ticket</span>
                </button>
              </div>
            </div>

            {/* Expandable Pre-Flight Detailed Checklist */}
            {showPreflightDetails && (
              <div className="mt-4 pt-4 border-t border-zinc-800/80 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs animate-in fade-in duration-150">
                <div className="bg-zinc-800/60 p-3 rounded-md border border-zinc-700/60 flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold text-white block">Vector &amp; Font Embedding</span>
                    <span className="text-[11px] text-zinc-400">All dynamic text and outlines rendered natively as crisp vector shapes.</span>
                  </div>
                </div>

                <div className="bg-zinc-800/60 p-3 rounded-md border border-zinc-700/60 flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold text-white block">Barcode &amp; QR Compliance</span>
                    <span className="text-[11px] text-zinc-400">{barcodeCount > 0 ? `${barcodeCount} dynamic symbologies (ISO/IEC certified module sizes)` : 'No barcodes in job'}</span>
                  </div>
                </div>

                <div className="bg-zinc-800/60 p-3 rounded-md border border-zinc-700/60 flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold text-white block">Bleed &amp; Crop Safety</span>
                    <span className="text-[11px] text-zinc-400">Bleed: {layout.bleed || 0} pt. Trim marks {layout.showTrimMarks ? 'enabled' : 'disabled'}.</span>
                  </div>
                </div>

                <div className="bg-zinc-800/60 p-3 rounded-md border border-zinc-700/60 flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold text-white block">Dataset Field Binding</span>
                    <span className="text-[11px] text-zinc-400">
                      {hasUnmappedVariables ? (
                        <span className="text-amber-300 font-bold">{unmappedVariables.length} unmapped variable tokens detected</span>
                      ) : (
                        `100% of ${csvData.headers.length} CSV columns mapped successfully`
                      )}
                    </span>
                  </div>
                </div>

                <div className="bg-zinc-800/60 p-3 rounded-md border border-zinc-700/60 flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold text-white block">Substrate &amp; Density</span>
                    <span className="text-[11px] text-zinc-400">{substrateName} ({substrateGsm} GSM) @ {format(unitCost)}/sheet</span>
                  </div>
                </div>

                <div className="bg-zinc-800/60 p-3 rounded-md border border-zinc-700/60 flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold text-white block">Press Calibration Marks</span>
                    <span className="text-[11px] text-zinc-400">{hasMarks ? 'Registration crosshairs, color wedges & slug included' : 'Minimalist clean output'}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Hardware Press & Plotter Profile Presets */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 sm:p-5 shadow-xs space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-zinc-800 pb-3">
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="w-4 h-4 text-orange-400" />
                <h4 className="font-extrabold text-sm text-zinc-100">Press &amp; Plotter Output Profiles</h4>
                <span className="text-[10px] bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-full font-bold border border-orange-500/30">
                  1-Click Auto-Configure
                </span>
              </div>
              <p className="text-xs text-zinc-400">Instantly applies optimal sheet size, margins, bleed, and mark calibrations for your hardware</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {EXPORT_HARDWARE_PROFILES.map((prof) => {
                const isActive = selectedProfileId === prof.id;
                return (
                  <button
                    key={prof.id}
                    type="button"
                    onClick={() => handleApplyProfile(prof.id)}
                    className={`p-3 rounded-lg border text-left transition-all cursor-pointer flex flex-col justify-between ${
                      isActive 
                        ? 'bg-zinc-800/90 border-orange-500 ring-1 ring-orange-500/50 shadow-md' 
                        : 'bg-zinc-950/60 border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800/40 text-zinc-300'
                    }`}
                  >
                    <div>
                      <div className="flex items-center justify-between gap-1 mb-1.5">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase font-mono ${
                          isActive ? 'bg-orange-500/20 text-orange-300' : 'bg-zinc-800 text-zinc-400'
                        }`}>
                          {prof.badge}
                        </span>
                        {isActive && <Check className="w-3.5 h-3.5 text-orange-400 shrink-0" />}
                      </div>
                      <span className="text-xs font-bold text-white block leading-snug">{prof.name}</span>
                    </div>
                    <span className="text-[11px] text-zinc-400 block mt-2 leading-relaxed">{prof.tagline}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Primary 3-Column Job Specification Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            
            {/* Card 1: Imposition & Sheet Geometry */}
            <div className="bg-zinc-900 rounded-md p-5 border border-zinc-800 shadow-2xs flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-extrabold text-zinc-200 text-xs uppercase tracking-wider flex items-center gap-1.5">
                    <Printer className="w-4 h-4 text-blue-600" />
                    Sheet &amp; Imposition Layout
                  </h4>
                  <span className="px-2 py-0.5 bg-blue-950/60 text-blue-300 border border-blue-800/60 text-[11px] font-bold rounded-md font-mono">
                    {layout.pageSize}
                  </span>
                </div>

                <dl className="space-y-2.5 text-xs">
                  <div className="flex justify-between items-center py-1 border-b border-zinc-800">
                    <dt className="text-zinc-500">Sticker Dimensions</dt>
                    <dd className="font-bold text-zinc-200 font-mono">{template.width} × {template.height} pt</dd>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-zinc-800">
                    <dt className="text-zinc-500">Imposition Grid</dt>
                    <dd className="font-bold text-blue-400 font-mono">{stickersPerPage}-Up ({rows}R × {cols}C)</dd>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-zinc-800">
                    <dt className="text-zinc-500">Master Sheets Needed</dt>
                    <dd className="font-extrabold text-zinc-100 font-mono text-sm">{totalPages.toLocaleString()} sheets</dd>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-zinc-800">
                    <dt className="text-zinc-500">Margins (X / Y)</dt>
                    <dd className="font-medium text-zinc-300 font-mono">{layout.marginX || 0} pt / {layout.marginY || 0} pt</dd>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <dt className="text-zinc-500">Bleed Allowance</dt>
                    <dd className="font-bold text-blue-400 font-mono">+{layout.bleed || 0} pt</dd>
                  </div>
                </dl>
              </div>

              {/* Interactive Mini Imposition Visual Thumbnail */}
              <div className="mt-4 pt-3 border-t border-zinc-800 bg-zinc-950 p-2.5 rounded-md flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {/* SVG Thumbnail Matrix */}
                  <svg className="w-10 h-14 bg-zinc-900 border border-zinc-700 rounded shadow-2xs p-0.5" viewBox="0 0 100 140">
                    <rect x="2" y="2" width="96" height="136" fill="#18181b" stroke="#3f3f46" strokeWidth="1" />
                    {Array.from({ length: rows }).map((_, r) => (
                      Array.from({ length: cols }).map((_, c) => {
                        const cellW = (90 - (cols - 1) * 2) / cols;
                        const cellH = (120 - (rows - 1) * 2) / rows;
                        const x = 5 + c * (cellW + 2);
                        const y = 8 + r * (cellH + 2);
                        return (
                          <rect
                            key={`${r}-${c}`}
                            x={x}
                            y={y}
                            width={cellW}
                            height={cellH}
                            fill="#3b82f6"
                            opacity="0.85"
                            rx="1"
                          />
                        );
                      })
                    ))}
                    {layout.printSlug && (
                      <rect x="5" y="132" width="90" height="3" fill="#64748b" rx="0.5" />
                    )}
                  </svg>
                  <div>
                    <span className="text-[11px] font-bold text-zinc-200 block">Matrix Visualizer</span>
                    <span className="text-[10px] text-zinc-500 block">{rows} rows × {cols} columns</span>
                  </div>
                </div>
                <span className="text-[10px] font-bold text-emerald-400 bg-emerald-950/40 px-2 py-1 rounded-md border border-emerald-900/50">
                  {layout.pageOrientation || 'Portrait'}
                </span>
              </div>
            </div>

            {/* Card 2: Material & Substrate Economics (Real-Time Currency Auto-Sync) */}
            <div id="tour-production-roi" className="bg-zinc-900 rounded-md p-5 border border-zinc-800 shadow-2xs flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-extrabold text-zinc-200 text-xs uppercase tracking-wider flex items-center gap-1.5">
                    <Layers className="w-4 h-4 text-blue-500" />
                    Substrate &amp; Cost Analysis
                  </h4>
                  <button
                    type="button"
                    onClick={() => setShowSubstrateModal(true)}
                    className="text-[10px] font-bold text-blue-400 hover:text-blue-300 bg-blue-950/40 hover:bg-blue-950/60 border border-blue-900/40 px-2 py-0.5 rounded-md transition-colors cursor-pointer"
                  >
                    Edit Rates
                  </button>
                </div>

                <dl className="space-y-2.5 text-xs">
                  <div className="flex justify-between items-center py-1 border-b border-zinc-800">
                    <dt className="text-zinc-500">Active Material</dt>
                    <dd className="font-bold text-zinc-200 max-w-[140px] truncate text-right" title={substrateName}>
                      {substrateName}
                    </dd>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-zinc-800">
                    <dt className="text-zinc-500">Weight &amp; Grammage</dt>
                    <dd className="font-bold text-zinc-200 font-mono">{substrateGsm} GSM ({totalPaperWeightKg} kg total)</dd>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-zinc-800">
                    <dt className="text-zinc-500">Rate / Master Sheet</dt>
                    <dd className="font-extrabold text-blue-400 font-mono">{format(unitCost)}</dd>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-zinc-800">
                    <dt className="text-zinc-500">Material Savings</dt>
                    <dd className="font-bold text-emerald-400 font-mono">+{format(savedMoney)} ({wasteReductionPct}%)</dd>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <dt className="text-zinc-500">Total Run Cost</dt>
                    <dd className="font-black text-zinc-100 font-mono text-sm">{format(totalJobCost, undefined, true)}</dd>
                  </div>
                </dl>
              </div>

              <div className="mt-4 pt-3 border-t border-zinc-800 bg-emerald-950/40 border border-emerald-900/30 p-2.5 rounded-md flex items-center justify-between text-xs">
                <span className="text-[11px] text-emerald-300 font-medium">Finished Cost / Sticker:</span>
                <span className="font-mono font-black text-emerald-400 text-sm">
                  {format(selectedRecords > 0 ? (totalJobCost / selectedRecords) : 0)}
                </span>
              </div>
            </div>

            {/* Card 3: Press Engine & Output Configuration */}
            <div className="bg-zinc-900 rounded-md p-5 border border-zinc-800 shadow-2xs flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-extrabold text-zinc-200 text-xs uppercase tracking-wider flex items-center gap-1.5">
                    <ShieldCheck className="w-4 h-4 text-purple-500" />
                    Press Engine &amp; Marks
                  </h4>
                  <span className="px-2 py-0.5 bg-purple-950/40 border border-purple-900/50 text-purple-300 text-[11px] font-bold rounded-md font-mono">
                    Vector HD
                  </span>
                </div>

                <dl className="space-y-2.5 text-xs">
                  <div className="flex justify-between items-center py-1 border-b border-zinc-800">
                    <dt className="text-zinc-500">Variable Elements</dt>
                    <dd className="font-bold text-zinc-200 font-mono">{template.elements.length} dynamic layers</dd>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-zinc-800">
                    <dt className="text-zinc-500">Dynamic Barcodes/QRs</dt>
                    <dd className="font-bold text-purple-400 font-mono">{barcodeCount} elements</dd>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-zinc-800">
                    <dt className="text-zinc-500">Color Calibration Wedge</dt>
                    <dd className="font-medium text-zinc-200">{layout.colorBars ? '✓ Enabled' : 'None'}</dd>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-zinc-800">
                    <dt className="text-zinc-500">Registration Marks</dt>
                    <dd className="font-medium text-zinc-200">{layout.registrationMarks ? '✓ 4-Corner Crosshairs' : 'None'}</dd>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <dt className="text-zinc-500">Est. Press Run Time</dt>
                    <dd className="font-bold text-zinc-200 font-mono">
                      {pressRunMinutes < 1 ? `~${Math.round(pressRunMinutes * 60)} seconds` : `~${pressRunMinutes.toFixed(1)} mins`}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="mt-4 pt-3 border-t border-zinc-800 bg-purple-950/40 border border-purple-900/30 p-2.5 rounded-md flex items-center justify-between text-xs">
                <span className="text-[11px] text-purple-300 font-medium">Export Mode:</span>
                <span className="font-bold text-purple-400">
                  {layout.exportMode === 'separate_pdfs' ? 'ZIP Archive' : 'Multi-Page Master PDF'}
                </span>
              </div>
            </div>

          </div>

          {/* Interactive Recharts Pre-Press ROI, Ink & Paper Yield Visualizer */}
          <div className="bg-zinc-900 rounded-md border border-zinc-800 p-5 shadow-2xs">
            <ProductionRoiVisualizer
              layout={layout}
              template={template}
              csvData={csvData}
              selectedRecordsCount={selectedRecords}
              totalPages={totalPages}
              onUpdateLayout={(key, val) => setLayout({ ...layout, [key]: val })}
            />
          </div>

          {/* Pre-Press Job Docket & Custom Output Configuration Form */}
          <div className="bg-zinc-900 rounded-md border border-zinc-800 p-6 shadow-2xs space-y-5">
            <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
              <div>
                <h4 className="font-extrabold text-zinc-100 text-sm flex items-center gap-2">
                  <FileCheck2 className="w-4 h-4 text-blue-500" />
                  Pre-Press Job Metadata &amp; Batch Range
                </h4>
                <p className="text-xs text-zinc-400">Customize work order details, client tagging, and dynamic record slicing</p>
              </div>

              <button
                type="button"
                onClick={() => setWorkOrderNumber(`WO-${Math.floor(100000 + Math.random() * 900000)}`)}
                className="px-2.5 py-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-bold rounded-md transition-colors cursor-pointer"
              >
                Regenerate WO #
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-bold text-zinc-300 block mb-1">Work Order / Ticket #</label>
                <input
                  type="text"
                  value={workOrderNumber}
                  onChange={(e) => setWorkOrderNumber(e.target.value)}
                  placeholder="e.g. WO-89210"
                  className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-md text-xs font-bold font-mono text-zinc-100 outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-zinc-300 block mb-1">Client / Brand Account</label>
                <input
                  type="text"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="e.g. Acme Organic Goods"
                  className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-md text-xs font-bold text-zinc-100 outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-zinc-300 block mb-1">Special Press Operator Notes</label>
                <input
                  type="text"
                  value={operatorNotes}
                  onChange={(e) => setOperatorNotes(e.target.value)}
                  placeholder="e.g. Matte lamination, Kiss-cut shape #2"
                  className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-md text-xs font-medium text-zinc-100 outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>
            </div>

            {/* Record Slicing & File Naming */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              
              {/* Record Range */}
              <div className="bg-zinc-950 p-4 rounded-md border border-zinc-800">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-zinc-200">Export Record Range</span>
                  <span className="text-xs font-bold text-blue-600 font-mono">
                    {selectedRecords} of {totalRecords} records selected
                  </span>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="text-[11px] text-zinc-500 block mb-1">From Record #</label>
                    <input 
                      type="number" 
                      min="1" 
                      max={totalRecords} 
                      value={exportStart}
                      onChange={(e) => setExportStart(Math.max(1, Math.min(exportEnd, Number(e.target.value))))}
                      className="w-full px-3 py-1.5 bg-zinc-900 border border-zinc-800 rounded-md text-xs font-bold font-mono text-zinc-100 outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                  <div className="text-zinc-400 font-bold mt-4">→</div>
                  <div className="flex-1">
                    <label className="text-[11px] text-zinc-500 block mb-1">To Record #</label>
                    <input 
                      type="number" 
                      min={exportStart} 
                      max={totalRecords} 
                      value={exportEnd}
                      onChange={(e) => setExportEnd(Math.max(exportStart, Math.min(totalRecords, Number(e.target.value))))}
                      className="w-full px-3 py-1.5 bg-zinc-900 border border-zinc-800 rounded-md text-xs font-bold font-mono text-zinc-100 outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                </div>
              </div>

              {/* Dynamic Batch File Naming */}
              <div className="bg-zinc-950 p-4 rounded-md border border-zinc-800">
                <span className="text-xs font-bold text-zinc-200 block mb-2">Batch File Naming Template</span>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <select 
                      className="w-full px-2.5 py-1.5 bg-zinc-900 border border-zinc-700 rounded-md text-xs font-bold text-zinc-200 outline-none focus:ring-2 focus:ring-blue-600"
                      value={layout.fileNameColumn || ''}
                      onChange={(e) => setLayout({ ...layout, fileNameColumn: e.target.value })}
                    >
                      <option value="">-- Record Number (Default) --</option>
                      {csvData.headers.map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1">
                    <input 
                      type="text" 
                      placeholder="Suffix e.g. -PrintReady"
                      value={layout.fileNameSlug || ''}
                      onChange={(e) => setLayout({ ...layout, fileNameSlug: e.target.value })}
                      className="w-full px-2.5 py-1.5 bg-zinc-900 border border-zinc-700 rounded-md text-xs font-bold text-zinc-200 outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-zinc-500 mt-1.5 font-mono truncate">
                  Output: {layout.fileNameColumn ? `[${layout.fileNameColumn}]` : (workOrderNumber || 'record_1')}{layout.fileNameSlug || ''}.pdf
                </p>
              </div>
            </div>

            {hasUnmappedVariables && (
              <div className="flex items-start gap-3 p-4 rounded-md bg-amber-950/40 border border-amber-800/60 text-amber-200 text-xs">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5 text-amber-500" />
                <div>
                  <h5 className="font-bold mb-0.5 text-amber-100">Unmapped Dynamic Variables Detected</h5>
                  <p>
                    Some text fields contain {"{variable}"} tokens that were not found in your CSV header columns:
                    {' '}<strong>{unmappedVariables.map(v => v.text).join(', ')}</strong>. These will be rendered as static text.
                  </p>
                </div>
              </div>
            )}
            
            {highVolumeWarning && (
              <div className="flex items-start gap-3 p-4 rounded-md bg-blue-950/40 border border-blue-800/60 text-blue-200 text-xs">
                <Info className="w-5 h-5 shrink-0 mt-0.5 text-blue-400" />
                <div>
                  <h5 className="font-bold mb-0.5 text-blue-100">High Volume Production Run ({totalRecords} records)</h5>
                  <p>
                    This job will compile {totalPages} full master sheets. The server will stream high-resolution vector PDF streams with automated memory optimization.
                  </p>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-center gap-3 p-4 rounded-md bg-red-950/40 border border-red-800/60 text-red-200 text-xs font-medium">
              <AlertCircle className="w-5 h-5 shrink-0 text-red-400" />
              <span>{error}</span>
            </div>
          )}

          {/* Animated Multi-Stage Progress HUD */}
          {(isExporting || isExportingProof) && (
            <div className="bg-zinc-900 text-white p-5 rounded-md border border-zinc-800 shadow-xl space-y-3 animate-in fade-in duration-200">
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />
                  <span className="font-bold text-white">
                    {isExportingProof ? 'Generating Digital Soft Proof Sheet...' : 'Generating Master Print-Ready PDF/X-1a...'}
                  </span>
                </div>
                <span className="font-mono font-bold text-emerald-400">{exportProgressPercent}%</span>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-zinc-800 rounded-full h-2 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-orange-500 to-orange-500 h-full rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${exportProgressPercent}%` }}
                />
              </div>

              <p className="text-[11px] text-zinc-400 font-mono">
                {exportProgressStage || 'Processing variable data streams...'}
              </p>
            </div>
          )}

          {/* Export Action Suite */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            
            {/* Action 1: Digital Soft Proof */}
            <button 
              type="button"
              onClick={() => handleExport(true)}
              disabled={isExporting || isExportingProof}
              className="flex items-center justify-center gap-2.5 py-4 px-4 bg-zinc-900 hover:bg-zinc-800 border-2 border-zinc-700 hover:border-zinc-600 text-zinc-200 rounded-md font-bold text-sm shadow-xs disabled:opacity-60 transition-all cursor-pointer group"
            >
              {isExportingProof ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                  <span>Rendering Proof...</span>
                </>
              ) : (
                <>
                  <Eye className="w-5 h-5 text-blue-600 group-hover:scale-110 transition-transform" />
                  <div className="text-left">
                    <span className="block font-bold">Download 1-Sheet Proof</span>
                    <span className="block text-[10px] text-zinc-400 font-normal">Fast digital soft proof preview</span>
                  </div>
                </>
              )}
            </button>

            {/* Action 2: Work Order Docket */}
            <button 
              id="tour-btn-job-ticket"
              type="button"
              onClick={() => setShowJobTicketModal(true)}
              className="flex items-center justify-center gap-2.5 py-4 px-4 bg-zinc-900 hover:bg-zinc-800 border-2 border-blue-600/50 hover:border-blue-500 text-blue-400 rounded-md font-bold text-sm shadow-xs transition-all cursor-pointer group"
            >
              <FileText className="w-5 h-5 text-blue-500 group-hover:scale-110 transition-transform" />
              <div className="text-left">
                <span className="block font-bold">Print Job Docket</span>
                <span className="block text-[10px] text-blue-300/80 font-normal">Print shop work order &amp; specs</span>
              </div>
            </button>
            
            {/* Action 3: Full Production Run */}
            <button 
              id="tour-btn-export-pdf"
              type="button"
              onClick={() => handleExport(false)}
              disabled={isExporting || isExportingProof}
              className="sm:col-span-2 lg:col-span-1 flex items-center justify-center gap-2.5 py-4 px-5 bg-gradient-to-r from-orange-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-md font-bold text-sm shadow-lg shadow-orange-600/30 disabled:opacity-60 transition-all cursor-pointer group"
            >
              {isExporting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin text-white" />
                  <span>Compiling PDF Engine...</span>
                </>
              ) : (
                <>
                  <Download className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
                  <div className="text-left">
                    <span className="block font-bold">Generate Production PDF</span>
                    <span className="block text-[10px] text-blue-100 font-normal">Full {totalPages} Master Sheets ({selectedRecords} pcs)</span>
                  </div>
                </>
              )}
            </button>
          </div>

          <p className="text-center text-zinc-400 text-xs">
            Commercial vector stream generates PDF/X-1a compatible files ready for high-speed digital presses, cutters &amp; plotters.
          </p>
        </div>
      )}

      {/* Substrate Rates & Library Customizer Modal */}
      <SubstrateCustomizerModal
        isOpen={showSubstrateModal}
        onClose={() => setShowSubstrateModal(false)}
        currentSubstrateId={layout.substrateType || 'standard_vinyl'}
        currentCostPerSheet={unitCost}
        currencySymbol={activeSymbol}
        onSelectSubstrate={(preset, cost) => {
          setLayout({
            ...layout,
            substrateType: preset.id,
            substrateName: preset.name,
            substrateGsm: preset.weightGsm,
            costPerSheet: cost !== undefined ? cost : preset.defaultCost
          });
        }}
      />

      {/* Printable Job Ticket / Pre-Press Work Order Docket Modal */}
      <JobTicketModal
        isOpen={showJobTicketModal}
        onClose={() => setShowJobTicketModal(false)}
        layout={layout}
        template={template}
        csvData={csvData}
        selectedRecords={selectedRecords}
        totalPages={totalPages}
        workOrderNumber={workOrderNumber}
        clientName={clientName}
        operatorNotes={operatorNotes}
      />
    </div>
  );
};
